import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, KeyRound, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
    const { orgCode } = useParams();
    const [searchParams] = useSearchParams();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    
    const [validating, setValidating] = useState(true);
    const [valid, setValid] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    
    const token = searchParams.get('token');

    useEffect(() => {
        if (token) {
            validateToken();
        } else {
            setValidating(false);
            setError('Kein Token gefunden');
        }
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await axios.get(`${API}/auth/validate-reset-token/${token}`);
            setValid(true);
            setEmail(response.data.email);
        } catch (err) {
            setError(err.response?.data?.detail || 'Ungültiger Link');
            setValid(false);
        } finally {
            setValidating(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Die Passwörter stimmen nicht überein');
            return;
        }
        
        setLoading(true);

        try {
            await axios.post(`${API}/auth/reset-password`, {
                token,
                new_password: password
            });
            
            setSuccess(true);
            toast.success('Passwort erfolgreich geändert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Passwort konnte nicht geändert werden');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg text-center">
                    <CardContent className="pt-12 pb-8">
                        <div 
                            className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                        >
                            <Loader2 
                                size={40} 
                                className="animate-spin"
                                style={{ color: tenant?.primary_color }} 
                            />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                            Link wird überprüft...
                        </h2>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    if (!valid) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg text-center">
                    <CardContent className="pt-12 pb-8">
                        <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 bg-red-50">
                            <AlertCircle size={40} className="text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                            Link ungültig
                        </h2>
                        <p className="text-slate-600 mb-6">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <Link to={`/o/${orgCode}/forgot-password`}>
                                <Button 
                                    className="w-full"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                >
                                    Neuen Link anfordern
                                </Button>
                            </Link>
                            <Link to={`/o/${orgCode}/login`}>
                                <Button variant="outline" className="w-full">
                                    <ArrowLeft size={16} className="mr-2" />
                                    Zur Anmeldung
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    if (success) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg text-center">
                    <CardContent className="pt-12 pb-8">
                        <div 
                            className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                        >
                            <CheckCircle2 size={40} style={{ color: tenant?.primary_color }} />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                            Passwort geändert!
                        </h2>
                        <p className="text-slate-600 mb-6">
                            Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.
                        </p>
                        <Link to={`/o/${orgCode}/login`}>
                            <Button 
                                className="w-full"
                                style={{ backgroundColor: tenant?.primary_color }}
                            >
                                Zur Anmeldung
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold">Neues Passwort festlegen</CardTitle>
                    <CardDescription>
                        Geben Sie ein neues Passwort für <strong>{email}</strong> ein.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Neues Passwort</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Mindestens 6 Zeichen"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="reset-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                placeholder="Passwort wiederholen"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="reset-confirm-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full"
                            style={{ backgroundColor: tenant?.primary_color }}
                            disabled={loading}
                            data-testid="reset-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Wird gespeichert...
                                </>
                            ) : (
                                <>
                                    <KeyRound size={16} className="mr-2" />
                                    Passwort speichern
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
